/**
 * 휴가 일수 미리보기 — 신청 전에 몇 일이 차감되는지 보여 준다. 확정 계산은 서버가 한다
 * (backend `app/attendance/days.py`).
 *
 * 종일 휴가는 근무일 수를 그대로 센다. 하루보다 짧게 쓰는 휴가는 그 시간을 하루 기준 근무시간으로
 * 나눈다 — **점심시간을 따로 빼지 않는다.** 09시부터 13시까지를 반차로 적는 것이 사무소 관행이고,
 * 거기서 점심 한 시간을 빼면 0.375일이 되어 아무도 기대하지 않는 숫자가 나온다.
 */
import { addDays, format, isWeekend, parseISO } from "date-fns";
import { DATE_FORMAT } from "@/lib/dates";

/** 시각을 고를 수 있는 간격(분). 서버도 30분 단위만 받는다. */
export const TIME_STEP_MINUTES = 30;

const toMinutes = (time: string) => {
  const [hour, minute] = time.split(":").map(Number);
  return hour * 60 + minute;
};

/** 근무일인가 — 주말도 휴일도 아닌 날. */
export const isWorkday = (date: string, holidays: ReadonlySet<string>) =>
  !isWeekend(parseISO(date)) && !holidays.has(date);

/** from 부터 to 까지(둘 다 포함)의 근무일 수. */
export function workdayCount(from: string, to: string, holidays: ReadonlySet<string>): number {
  let count = 0;
  for (let day = from; day <= to; day = format(addDays(parseISO(day), 1), DATE_FORMAT))
    if (isWorkday(day, holidays)) count++;
  return count;
}

/**
 * 시작일부터 근무일을 n 개 센 날 — 연차 n 일의 종료일. 시작일이 근무일이 아니면 다음 근무일부터 센다.
 * 1년을 넘게 세지 않는다(휴일 목록이 비었을 때 끝없이 돌지 않게).
 */
export function nthWorkday(from: string, n: number, holidays: ReadonlySet<string>): string {
  let day = from;
  let counted = 0;
  for (let guard = 0; guard < 366; guard++) {
    if (isWorkday(day, holidays)) counted++;
    if (counted >= n) return day;
    day = format(addDays(parseISO(day), 1), DATE_FORMAT);
  }
  return day;
}

/** 시각 범위의 시간 — 점심을 빼지 않는다. */
export const windowHours = (start: string, end: string) => (toMinutes(end) - toMinutes(start)) / 60;

/**
 * 차감 일수. 시각이 없으면 근무일 수, 있으면 그 시간 ÷ 하루 기준 시간(소수 둘째 자리에서 반올림).
 */
export function vacationDays(
  span: {
    start_date: string;
    end_date: string;
    start_time?: string | null;
    end_time?: string | null;
  },
  standardHours: number,
  holidays: ReadonlySet<string>
): number {
  const days = workdayCount(span.start_date, span.end_date, holidays);
  if (!span.start_time || !span.end_time) return days;
  if (!days) return 0;
  return Math.round((windowHours(span.start_time, span.end_time) / standardHours) * 100) / 100;
}

/**
 * 고를 수 있는 시(06–23). 분은 아래 목록으로 따로 고른다.
 *
 * 「06:00」 같은 한 덩어리가 아니라 시와 분을 나눈 이유: 한 덩어리면 선택지가 35개여서 원하는
 * 시각까지 한참 굴려야 한다. 나누면 18개와 2개다.
 */
export function hourOptions(from = 6, to = 23): string[] {
  const out: string[] = [];
  for (let hour = from; hour <= to; hour++) out.push(String(hour).padStart(2, "0"));
  return out;
}

/** 고를 수 있는 분 — 서버의 CHECK 가 0·30 만 받는다(backend `app/attendance/models.py`). */
export const MINUTE_OPTIONS: readonly string[] = ["00", String(TIME_STEP_MINUTES)];
